import Box from '@mui/material/Box';
import clsx from 'clsx';
import {Close} from '@mui/icons-material';
import {CircularProgress, IconButton, styled, Tooltip, tooltipClasses, TooltipProps, Typography} from '@mui/material';
import style from './hint.module.scss';
import {useTranslation} from 'react-i18next';

const HintTooltip = styled(({className, ...props}: TooltipProps) => (
    <Tooltip {...props} classes={{popper: className}} arrow/>
))(() => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: '#f5f5f9',
        color: 'rgba(0, 0, 0, 0.87)',
        minWidth: 300,
        maxWidth: 800,
        maxHeight: 500,
        paddingInline: 15,
        fontSize: 14,
        border: '1px solid #ababab',
    },
    [`& .${tooltipClasses.arrow}`]: {
        color: '#f5f5f9',
    },
    [`& .${tooltipClasses.arrow}::before`]: {
        border: '1px solid #ababab',
    },
}));

export interface HintViewData {
    title: string;
    content: string;
    location_description: string | null;
}

interface HintViewProps {
    hint: HintViewData | null;
    loading?: boolean;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    top?: string;
    left?: string;
    bottom?: string;
    right?: string;
    align?: 'topRight' | 'topLeft' | 'bottomLeft' | 'bottomRight';
    width?: string;
    height?: string;
    fontSize?: string;
    children: React.ReactNode;
}

function HintView(
    {
        hint,
        loading = false,
        open,
        onOpenChange,
        top,
        left,
        bottom,
        right,
        align = 'topRight',
        width,
        height,
        fontSize = '1rem',
        children,
    }: HintViewProps) {
    const {t} = useTranslation();

    const defaultAlign = !(!!top || !!left || !!bottom || !!right);
    const title = loading ? t('Loading...') : hint?.title;

    const loadingContent = (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, my: 1}}>
            <CircularProgress size={18}/>
            <Typography>{t('Hint is loading...')}</Typography>
        </Box>
    );

    const hintContent = hint ? (
        <>
            {hint.content && (
                <Box dangerouslySetInnerHTML={{__html: hint.content}}/>
            )}
        </>
    ) : null;

    const content = loading ? loadingContent : hintContent;

    return (
        <Box className={style.container} onClick={(event) => event.stopPropagation()}>
            <HintTooltip
                title={
                    <>
                        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <Typography>{title}</Typography>

                            <IconButton onClick={handleClose}>
                                <Close/>
                            </IconButton>
                        </Box>

                        {content}
                    </>
                }
                arrow
                disableHoverListener
                disableTouchListener
                open={open}
            >
                <IconButton
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        handleOpen();
                    }}
                    sx={{
                        '&&': {
                            top,
                            left,
                            bottom,
                            right,
                            width,
                            height,
                            border: '2px solid black',
                            borderRadius: '50px',
                            position: 'absolute',
                        },
                        color: 'black',
                        fontSize,
                    }}
                    className={clsx(style.hint, defaultAlign && style[align])}
                >
                    ?
                </IconButton>
            </HintTooltip>

            <Box>{children}</Box>
        </Box>
    );

    function handleOpen() {
        onOpenChange(!open);
    }

    function handleClose() {
        onOpenChange(false);
    }
}

export default HintView;
